/**
 * v2 (Project Independence) shared enums + labels (DNKADM-0049).
 *
 * Defined ONCE here and reused by the downstream v2 specs (services 0055,
 * reservations 0061, availability/staff). Two enums per the handoff:
 *
 *  - **Assignment mode** (§4.7) — how a service selects each of staff / resources:
 *    `NONE | MEMBER_SELECTS | ROUND_ROBIN`. Drives the service form, availability,
 *    and reservation create-on-behalf.
 *  - **Reservation status** — the seven lowercase wire values
 *    `new | unconfirmed | confirmed | cancelled | showed | no_show | invalid`, each with its
 *    "occupies a slot" / "counts toward daily cap" semantics.
 *
 * Chip colors reuse the shared status `{ fg, bg }` pairs (MUI's Chip can't theme
 * per-value colors), rendered via the existing `chipSx` helper — resolved
 * per mode through `resolveMaps` since DNKADM-0293 (the light-only `STATUS`
 * const painted light chips in dark mode). Labels are brand-neutral, human
 * display strings.
 */
import type { SemanticMaps } from '@/theme/tokens';

// ---- Assignment mode (handoff §4.7) ----

/** How a service selects a dimension (staff or resource). */
export type AssignmentMode = 'NONE' | 'MEMBER_SELECTS' | 'ROUND_ROBIN';

/** All assignment modes, in form-display order. */
export const ASSIGNMENT_MODES: readonly AssignmentMode[] = [
  'NONE',
  'MEMBER_SELECTS',
  'ROUND_ROBIN',
] as const;

const ASSIGNMENT_MODE_LABELS: Record<AssignmentMode, string> = {
  NONE: 'Not used',
  MEMBER_SELECTS: 'Member selects',
  ROUND_ROBIN: 'Auto-assign (round robin)',
};

/** Human label for an assignment mode (form/option display). */
export function assignmentModeLabel(mode: AssignmentMode): string {
  return ASSIGNMENT_MODE_LABELS[mode];
}

// ---- Reservation status (handoff §4.8) ----

/** A reservation status wire value (lowercase, as sent by dink-api). */
export type ReservationStatus =
  | 'new'
  | 'unconfirmed'
  | 'confirmed'
  | 'cancelled'
  | 'showed'
  | 'no_show'
  | 'invalid';

/** All reservation statuses, in lifecycle order. */
export const RESERVATION_STATUSES: readonly ReservationStatus[] = [
  'new',
  'unconfirmed',
  'confirmed',
  'cancelled',
  'showed',
  'no_show',
  'invalid',
] as const;

const RESERVATION_STATUS_LABELS: Record<ReservationStatus, string> = {
  new: 'New',
  unconfirmed: 'Pending confirmation',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  showed: 'Showed',
  no_show: 'No-show',
  invalid: 'Invalid',
};

/** Human label for a reservation status (chip/list display). */
export function reservationStatusLabel(status: ReservationStatus): string {
  return RESERVATION_STATUS_LABELS[status];
}

/** Which shared status pair each reservation status reuses (the semantics:
 * `confirmed`/`showed` → the green "active" pair, `cancelled`/`no_show` → the
 * warm "cancelled" pair, `invalid` → "paused", `new`/`unconfirmed` → "past_due"
 * amber) so the v2 chips match the rest of the app. */
const RESERVATION_STATUS_PAIR: Record<ReservationStatus, keyof SemanticMaps['status']> = {
  new: 'past_due',
  unconfirmed: 'past_due',
  confirmed: 'active',
  cancelled: 'cancelled',
  showed: 'active',
  no_show: 'cancelled',
  invalid: 'paused',
};

/**
 * Chip `{ fg, bg }` color pair for a reservation status (consume via `chipSx`).
 * Mode-aware since DNKADM-0293 — pass `resolveMaps(useTheme())`; the previous
 * static `RESERVATION_STATUS_CHIP` const baked the light-only `STATUS` values
 * and painted light chips in dark mode.
 */
export function reservationStatusChip(
  status: ReservationStatus,
  maps: SemanticMaps,
): { fg: string; bg: string } {
  const { fg, bg } = maps.status[RESERVATION_STATUS_PAIR[status]];
  return { fg, bg };
}

/**
 * Whether a reservation is still waiting on staff to confirm it (DNKADM-0474, V-51): an
 * `unconfirmed` request or a `new` hold. The one place that answers "is this Pending?" so a
 * surface never hand-picks one of the two (the DNKADM-0393 drawer bug). Case/space tolerant,
 * and false for null/unknown. Surfaces that mean ONLY the request queue (`unconfirmed`, the
 * confirmations list) or ONLY a payment hold (`new`, the decline dialog's hold path) stay on
 * the exact status: those are different questions.
 */
export function isAwaitingConfirmation(status: string | null | undefined): boolean {
  const s = status?.trim().toLowerCase();
  return s === 'new' || s === 'unconfirmed';
}

/**
 * Whether a reservation in this status OCCUPIES a slot (handoff §4.8): a
 * `confirmed`/`unconfirmed` booking, or a `new` payment hold that has not yet expired.
 * Expiry is a server-side TTL the client cannot see, so this returns the
 * status-level truth (`new`, `unconfirmed`, and `confirmed` occupy); a caller with expiry
 * information narrows `new` further.
 */
export function occupiesSlot(status: ReservationStatus): boolean {
  return status === 'confirmed' || status === 'unconfirmed' || status === 'new';
}

/**
 * Whether a reservation in this status COUNTS toward the daily cap: active `new` +
 * `unconfirmed` + `confirmed` + `showed`.
 */
export function countsTowardCap(status: ReservationStatus): boolean {
  return (
    status === 'new' ||
    status === 'unconfirmed' ||
    status === 'confirmed' ||
    status === 'showed'
  );
}
